import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import {
  billingApi,
  type SubscriptionTier,
  type CreditPack,
  type Subscription,
  type Invoice,
} from '@/api/billing';
import type { CreditBalance } from '@/types';

export function SubscriptionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [creditPacks, setCreditPacks] = useState<CreditPack[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [activeTab, setActiveTab] = useState<'subscription' | 'credits' | 'invoices'>('subscription');
  const [processingAction, setProcessingAction] = useState<string | null>(null);

  // Check for successful checkout
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      // Clear the session_id from URL
      navigate('/billing', { replace: true });
      // Reload data after successful checkout
      loadData();
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [tiersData, packsData, balanceData, invoicesData] = await Promise.all([
        billingApi.getTiers(),
        billingApi.getCreditPacks(),
        billingApi.getBalance(),
        billingApi.getInvoices(),
      ]);
      setTiers(tiersData);
      setCreditPacks(packsData);
      setBalance(balanceData);
      setInvoices(invoicesData);

      try {
        const subData = await billingApi.getSubscription();
        setSubscription(subData);
      } catch {
        // No subscription - that's okay
        setSubscription(null);
      }
    } catch (error) {
      console.error('Failed to load billing data:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSelectTier(tier: string) {
    setProcessingAction(tier);
    try {
      const session = await billingApi.createSubscriptionCheckout(tier);
      window.location.href = session.url;
    } catch (error) {
      console.error('Failed to create checkout:', error);
    } finally {
      setProcessingAction(null);
    }
  }

  async function handleBuyCreditPack(packId: string) {
    setProcessingAction(packId);
    try {
      const session = await billingApi.createCreditPackCheckout(packId);
      window.location.href = session.url;
    } catch (error) {
      console.error('Failed to create checkout:', error);
    } finally {
      setProcessingAction(null);
    }
  }

  async function handleManageSubscription() {
    setProcessingAction('portal');
    try {
      const session = await billingApi.createBillingPortal();
      window.location.href = session.url;
    } catch (error) {
      console.error('Failed to create portal session:', error);
    } finally {
      setProcessingAction(null);
    }
  }

  async function handleCancelSubscription() {
    if (!confirm('Are you sure you want to cancel your subscription? You will continue to have access until the end of your billing period.')) {
      return;
    }
    setProcessingAction('cancel');
    try {
      await billingApi.cancelSubscription();
      await loadData();
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
    } finally {
      setProcessingAction(null);
    }
  }

  async function handleReactivateSubscription() {
    setProcessingAction('reactivate');
    try {
      await billingApi.reactivateSubscription();
      await loadData();
    } catch (error) {
      console.error('Failed to reactivate subscription:', error);
    } finally {
      setProcessingAction(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Billing & Subscription</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Manage your subscription, credits, and billing information.
        </p>
      </div>

      {/* Credit Balance Overview */}
      {balance && (
        <Card className="mb-8 p-6 bg-gradient-to-br from-gray-800 via-gray-900 to-black text-white border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-400">Available Credits</p>
              <p className="text-4xl font-bold">{(balance.balance ?? 0).toLocaleString()}</p>
              <p className="text-sm text-gray-400 mt-1">
                {(balance.monthlyCredits ?? 0).toLocaleString()} credits/month included
              </p>
            </div>
            <div className="text-right">
              <Badge variant="default" className="bg-white/10 text-white border border-gray-600">
                {balance.tier ?? 'Free'}
              </Badge>
              {balance.resetAt && (
                <p className="text-sm text-gray-400 mt-2">
                  Resets {new Date(balance.resetAt).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex space-x-1 mb-6 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
        {(['subscription', 'credits', 'invoices'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
              activeTab === tab
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Subscription Tab */}
      {activeTab === 'subscription' && (
        <div>
          {/* Current Subscription */}
          {subscription && (
            <Card className="mb-8 p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Current Plan: {subscription.tierName}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mt-1">
                    ${subscription.pricePerMonth}/month - {subscription.monthlyCredits.toLocaleString()} credits
                  </p>
                  <div className="mt-2 flex items-center space-x-2">
                    <Badge variant={subscription.status === 'Active' ? 'success' : 'warning'}>
                      {subscription.statusName}
                    </Badge>
                    {subscription.cancelAtPeriodEnd && (
                      <Badge variant="warning">Canceling at period end</Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
                    Current period: {new Date(subscription.currentPeriodStart).toLocaleDateString()} -{' '}
                    {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                  </p>
                </div>
                <div className="space-x-2">
                  <Button
                    variant="outline"
                    onClick={handleManageSubscription}
                    disabled={processingAction === 'portal'}
                  >
                    {processingAction === 'portal' ? 'Loading...' : 'Manage Billing'}
                  </Button>
                  {subscription.cancelAtPeriodEnd ? (
                    <Button
                      onClick={handleReactivateSubscription}
                      disabled={processingAction === 'reactivate'}
                    >
                      {processingAction === 'reactivate' ? 'Reactivating...' : 'Reactivate'}
                    </Button>
                  ) : (
                    <Button
                      variant="danger"
                      onClick={handleCancelSubscription}
                      disabled={processingAction === 'cancel'}
                    >
                      {processingAction === 'cancel' ? 'Canceling...' : 'Cancel Subscription'}
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          )}

          {/* Available Plans */}
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            {subscription ? 'Change Plan' : 'Choose a Plan'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tiers.map((tier) => (
              <Card
                key={tier.tier}
                className={`p-6 relative ${
                  tier.isPopular ? 'ring-2 ring-violet-500' : ''
                } ${subscription?.tier === tier.tier ? 'bg-violet-50 dark:bg-violet-900/20' : ''}`}
              >
                {tier.isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge variant="primary">Most Popular</Badge>
                  </div>
                )}
                <div className="text-center mb-4">
                  <h4 className="text-xl font-bold text-gray-900 dark:text-white">{tier.name}</h4>
                  <div className="mt-2">
                    <span className="text-3xl font-bold">${tier.pricePerMonth}</span>
                    <span className="text-gray-500">/month</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {tier.monthlyCredits.toLocaleString()} credits/month
                  </p>
                </div>
                <ul className="space-y-2 mb-6">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start text-sm text-gray-600 dark:text-gray-400">
                      <svg className="w-4 h-4 mr-2 text-green-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button
                  className="w-full"
                  variant={tier.isPopular ? 'primary' : 'outline'}
                  disabled={
                    subscription?.tier === tier.tier ||
                    tier.tier === 'Free' ||
                    processingAction === tier.tier
                  }
                  onClick={() => handleSelectTier(tier.tier)}
                >
                  {subscription?.tier === tier.tier
                    ? 'Current Plan'
                    : tier.tier === 'Free'
                    ? 'Free Tier'
                    : processingAction === tier.tier
                    ? 'Processing...'
                    : 'Select Plan'}
                </Button>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Credits Tab */}
      {activeTab === 'credits' && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Buy Credit Packs</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Need more credits? Purchase additional credit packs anytime.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {creditPacks.map((pack) => (
              <Card
                key={pack.packId}
                className={`p-6 ${pack.isBestValue ? 'ring-2 ring-green-500' : ''}`}
              >
                {pack.isBestValue && (
                  <Badge variant="success" className="mb-2">Best Value</Badge>
                )}
                <h4 className="text-lg font-bold text-gray-900 dark:text-white">{pack.name}</h4>
                <div className="mt-2">
                  <span className="text-2xl font-bold">${pack.price}</span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {pack.totalCredits.toLocaleString()} credits
                </p>
                {pack.bonusCredits > 0 && (
                  <p className="text-xs text-green-600 dark:text-green-400">
                    +{pack.bonusCredits.toLocaleString()} bonus
                  </p>
                )}
                {pack.savingsPercentage > 0 && (
                  <Badge variant="success" className="mt-2">Save {pack.savingsPercentage}%</Badge>
                )}
                <Button
                  className="w-full mt-4"
                  variant={pack.isBestValue ? 'primary' : 'outline'}
                  disabled={processingAction === pack.packId}
                  onClick={() => handleBuyCreditPack(pack.packId)}
                >
                  {processingAction === pack.packId ? 'Processing...' : 'Buy Now'}
                </Button>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Invoices Tab */}
      {activeTab === 'invoices' && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Invoice History</h3>
          {invoices.length === 0 ? (
            <Card className="p-8 text-center">
              <p className="text-gray-500 dark:text-gray-400">No invoices yet</p>
            </Card>
          ) : (
            <Card className="overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {invoice.invoiceNumber || invoice.id.slice(0, 8)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {new Date(invoice.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        ${invoice.amountPaid.toFixed(2)} {invoice.currency.toUpperCase()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={invoice.status === 'paid' ? 'success' : 'warning'}>
                          {invoice.status}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        {invoice.pdfUrl && (
                          <a
                            href={invoice.pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-violet-600 hover:text-violet-800 dark:text-violet-400"
                          >
                            Download PDF
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
